/* Wishlist Hero の PDP 標準ボタン結果をテーマ共通トーストへ接続する */
(() => {
  if (window.AuremePdpWishlistToast) return;

  const wishlistSdk = window.AuremeWishlistSdk;
  if (!wishlistSdk) {
    console.error('[aureme-pdp-wishlist-toast] Wishlist Hero SDK helper is not loaded.');
    return;
  }

  const buttonState = window.AuremePdpWishlistButtonState;
  if (!buttonState) {
    console.error('[aureme-pdp-wishlist-toast] PDP wishlist button state helper is not loaded.');
    return;
  }

  const initialSync = window.AuremePdpWishlistInitialSync;
  if (!initialSync) {
    console.error('[aureme-pdp-wishlist-toast] PDP wishlist initial sync helper is not loaded.');
    return;
  }

  const createReadyRetryScheduler = window.AuremeReadyRetry?.createScheduler;
  if (typeof createReadyRetryScheduler !== 'function') {
    console.error('[aureme-pdp-wishlist-toast] ready retry helper is not loaded.');
    return;
  }

  const SDK_READY_EVENT = 'wishlist-hero-wishlist-sdk-ready';
  const PDP_OPERATION_TIMEOUT_MS = 10000;
  const PDP_SUCCESS_FALLBACK_DELAY_MS = 1200;
  const PDP_EXTERNAL_NOTIFICATION_SUPPRESSION_CLASS = 'aureme-pdp-wishlist-notification-suppressed';
  const PDP_EXTERNAL_NOTIFICATION_SUPPRESS_MS = 5500;
  const SDK_PATCH_RETRY_INTERVAL_MS = 100;
  const SDK_PATCH_MAX_RETRY_COUNT = 100;
  const sdkPatchRetryScheduler = createReadyRetryScheduler({
    intervalMs: SDK_PATCH_RETRY_INTERVAL_MS,
    maxRetryCount: SDK_PATCH_MAX_RETRY_COUNT,
  });

  let externalNotificationSuppressionTimer = null;
  const {
    getWishlistErrorToastMessage,
    getWishlistMutationMethodName,
    getWishlistSuccessToastMessage,
    showToast,
    wishlistAddMethodName,
    wishlistDeleteMethodName,
  } = wishlistSdk;

  function isCustomerLoggedIn() {
    const config = window.Aureme?.config || {};
    return Boolean(config.customerLoggedIn || config.customerId);
  }

  function openPdpLoginModal(opener = null) {
    if (window.AuremeWishlistLoginModal?.open?.(opener)) {
      return true;
    }

    console.error('[aureme-pdp-wishlist-toast] wishlist login modal helper is not loaded.');
    return false;
  }

  function handlePdpLoggedOutClick(event, opener) {
    if (isCustomerLoggedIn()) return false;
    if (!openPdpLoginModal(opener)) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    return true;
  }

  function suppressWishlistHeroNotifications() {
    if (!document.body) return;

    /*
      標準 PDP ボタン操作時だけ Wishlist Hero の標準通知を隠す。
      同じ PDP 内の別ボタン通知を巻き込み続けないよう、短時間で抑止を解除する。
    */
    document.body.classList.add(PDP_EXTERNAL_NOTIFICATION_SUPPRESSION_CLASS);
    if (externalNotificationSuppressionTimer) {
      window.clearTimeout(externalNotificationSuppressionTimer);
    }

    externalNotificationSuppressionTimer = window.setTimeout(() => {
      document.body?.classList.remove(PDP_EXTERNAL_NOTIFICATION_SUPPRESSION_CLASS);
      externalNotificationSuppressionTimer = null;
    }, PDP_EXTERNAL_NOTIFICATION_SUPPRESS_MS);
  }

  const pdpInitialSyncController = initialSync.createController({
    onRemoveError() {
      showToast(getWishlistErrorToastMessage(true), 'error');
    },
    onRemoveSuccess(isAdded) {
      showToast(getWishlistSuccessToastMessage(isAdded), 'success');
    },
    suppressExternalNotifications: suppressWishlistHeroNotifications,
  });

  /*
    DOM 反転と SDK 成否を同じ操作として扱うため、
    クリック時点の状態から期待する mutation 名を固定しておく。
  */
  function createPendingPdpWishlistOperation(container) {
    const initialIsAdded = buttonState.isAdded(container);
    if (initialIsAdded === null) return null;

    return {
      container,
      expectedMethodName: getWishlistMutationMethodName(initialIsAdded),
      fallbackSuccessTimerId: null,
      initialIsAdded,
      nextIsAdded: null,
      observer: null,
      sdkMutationStarted: false,
      sdkMutationSucceeded: false,
      timeoutId: null,
    };
  }

  function startPendingPdpWishlistOperation(operation, { onDomChange, onTimeout }) {
    operation.observer = new MutationObserver(onDomChange);
    operation.timeoutId = window.setTimeout(onTimeout, PDP_OPERATION_TIMEOUT_MS);
    operation.observer.observe(operation.container, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  function clearPdpFallbackSuccessTimer(operation) {
    if (!operation?.fallbackSuccessTimerId) return;

    window.clearTimeout(operation.fallbackSuccessTimerId);
    operation.fallbackSuccessTimerId = null;
  }

  function clearPendingPdpWishlistOperation(operation) {
    if (operation?.timeoutId) {
      window.clearTimeout(operation.timeoutId);
    }
    clearPdpFallbackSuccessTimer(operation);
    operation?.observer?.disconnect();
  }

  function recordPdpStateChangeIfNeeded(operation) {
    if (!operation) return false;

    const currentIsAdded = buttonState.isAdded(operation.container);
    if (currentIsAdded === null) return false;
    if (currentIsAdded === operation.initialIsAdded) {
      operation.nextIsAdded = null;
      clearPdpFallbackSuccessTimer(operation);
      return false;
    }

    operation.nextIsAdded = currentIsAdded;
    buttonState.normalizeIfNeeded(operation.container, currentIsAdded);
    suppressWishlistHeroNotifications();
    return true;
  }

  function isPdpOperationSuccessReady(operation, { allowDomFallback = false } = {}) {
    if (!operation || operation.nextIsAdded === null) return false;
    if (operation.sdkMutationStarted && !operation.sdkMutationSucceeded) return false;
    if (!operation.sdkMutationStarted && !allowDomFallback) return false;

    return true;
  }

  function createPdpWishlistOperation() {
    let pendingOperation = null;

    function clearPendingOperation() {
      clearPendingPdpWishlistOperation(pendingOperation);
      pendingOperation = null;
    }

    function completeSuccessIfReady({ allowDomFallback = false } = {}) {
      const operation = pendingOperation;
      if (!isPdpOperationSuccessReady(operation, { allowDomFallback })) return false;

      /*
        Wishlist Hero 標準通知は CSS 側で抑止し、テーマ共通トーストだけを表示する。
        登録時は右下 popup と併用する。
      */
      buttonState.normalizeIfNeeded(operation.container, operation.nextIsAdded);
      showToast(getWishlistSuccessToastMessage(operation.nextIsAdded), 'success');
      clearPendingOperation();
      return true;
    }

    function scheduleSuccessFallback(operation) {
      if (operation.sdkMutationStarted || operation.fallbackSuccessTimerId) return;

      /*
        標準ボタンが共有 SDK helper を通らない場合だけ、DOM 状態変化を成功として扱う。
        SDK 呼び出しを捕捉できた場合は、reject 時のエラー表示を優先する。
        バックエンド成功を保証できないため、SDK 経路が取れない場合の補助判定に留める。
      */
      operation.fallbackSuccessTimerId = window.setTimeout(() => {
        operation.fallbackSuccessTimerId = null;
        completeSuccessIfReady({ allowDomFallback: true });
      }, PDP_SUCCESS_FALLBACK_DELAY_MS);
    }

    function handleStateChange() {
      if (!recordPdpStateChangeIfNeeded(pendingOperation)) return false;

      const operation = pendingOperation;
      if (operation.sdkMutationStarted) return completeSuccessIfReady();

      scheduleSuccessFallback(operation);
      return false;
    }

    function schedulePostClickStateChecks() {
      /*
        Wishlist Hero はクリック処理中にクラス、path、viewBox を段階的に書き換える。
        MutationObserver だけでは初回の中間状態を拾えないことがあるため、イベント完了直後にも補正する。
      */
      const checkState = () => {
        handleStateChange();
      };

      if (typeof window.queueMicrotask === 'function') {
        window.queueMicrotask(checkState);
      } else {
        window.setTimeout(checkState, 0);
      }

      window.requestAnimationFrame?.(checkState);
      window.setTimeout(checkState, 50);
    }

    function expirePendingOperation() {
      if (!pendingOperation) return;
      recordPdpStateChangeIfNeeded(pendingOperation);
      if (completeSuccessIfReady({ allowDomFallback: true })) return;
      clearPendingOperation();
    }

    function mark(container) {
      const operation = createPendingPdpWishlistOperation(container);
      if (!operation) return;

      suppressWishlistHeroNotifications();
      clearPendingOperation();
      pendingOperation = operation;
      startPendingPdpWishlistOperation(operation, {
        onDomChange: handleStateChange,
        onTimeout: expirePendingOperation,
      });
      schedulePostClickStateChecks();
    }

    function isCurrentOperation(operation) {
      return Boolean(operation && operation === pendingOperation);
    }

    return {
      getMatchingOperation(methodName) {
        return pendingOperation?.expectedMethodName === methodName ? pendingOperation : null;
      },
      handleMutationRejected(operation, errorMessage) {
        if (!isCurrentOperation(operation)) return;

        showToast(errorMessage, 'error');
        clearPendingOperation();
      },
      handleMutationStarted(operation) {
        if (!isCurrentOperation(operation)) return;

        operation.sdkMutationStarted = true;
        clearPdpFallbackSuccessTimer(operation);
      },
      handleMutationSucceeded(operation) {
        if (!isCurrentOperation(operation)) return;

        operation.sdkMutationSucceeded = true;
        handleStateChange();
      },
      mark,
    };
  }

  const pdpWishlistOperation = createPdpWishlistOperation();

  function handlePdpVariantStateChange() {
    buttonState.scheduleStateChecks();
  }

  function subscribePdpVariantStateChanges() {
    document.addEventListener('product-info:loaded', handlePdpVariantStateChange);

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, handlePdpVariantStateChange);
    subscribe(PUB_SUB_EVENTS.variantChange, handlePdpVariantStateChange);
  }

  function patchWishlistMethod(sdk, methodName, { errorMessage, operationManager }) {
    /*
     * Wishlist Hero 標準ボタンの DOM 変化と SDK 成否を同じ pending operation に紐づけ、
     * DOM だけ先に反転した場合でも reject 時は成功トーストよりエラー表示を優先する。
     */
    const originalMethod = sdk?.[methodName];
    if (typeof originalMethod !== 'function') return false;
    if (originalMethod.auremePdpWishlistToastPatched) return true;

    const wrappedMethod = async function (...args) {
      const operation = operationManager.getMatchingOperation(methodName);
      operationManager.handleMutationStarted(operation);

      try {
        const result = await originalMethod.apply(this, args);
        operationManager.handleMutationSucceeded(operation);
        return result;
      } catch (error) {
        operationManager.handleMutationRejected(operation, errorMessage);
        throw error;
      }
    };

    wrappedMethod.auremePdpWishlistToastPatched = true;
    sdk[methodName] = wrappedMethod;
    return true;
  }

  function patchWishlistSdkMethods(sdk, operationManager) {
    /* Wishlist Hero SDK のメソッド名に依存するため、SDK 更新時はここを回帰確認する */
    const didPatchAdd = patchWishlistMethod(sdk, wishlistAddMethodName, {
      errorMessage: getWishlistErrorToastMessage(false),
      operationManager,
    });
    const didPatchDelete = patchWishlistMethod(sdk, wishlistDeleteMethodName, {
      errorMessage: getWishlistErrorToastMessage(true),
      operationManager,
    });

    return didPatchAdd && didPatchDelete;
  }

  function patchWishlistSdk() {
    const sdk = window.WishListHero_SDK;
    if (!sdk) return false;

    return patchWishlistSdkMethods(sdk, pdpWishlistOperation);
  }

  function tryPatchWishlistSdk() {
    if (!patchWishlistSdk()) return false;

    sdkPatchRetryScheduler.clear();
    document.removeEventListener(SDK_READY_EVENT, handleWishlistSdkReady);
    return true;
  }

  function handleWishlistSdkReady() {
    scheduleWishlistSdkPatch();
  }

  function scheduleWishlistSdkPatch(retryCount = 0) {
    sdkPatchRetryScheduler.schedule({
      tryReady: tryPatchWishlistSdk,
      onRetry: scheduleWishlistSdkPatch,
      retryCount,
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const container = buttonState.getContainerFromTarget(target);
      if (!container) return;

      const clickedButton = buttonState.getInteractiveElement(target, container);
      if (!clickedButton) return;

      if (handlePdpLoggedOutClick(event, clickedButton)) return;
      if (pdpInitialSyncController.handleRemoveClick(event, container)) return;

      tryPatchWishlistSdk();
      pdpWishlistOperation.mark(container);
    },
    true,
  );

  if (!tryPatchWishlistSdk()) {
    document.addEventListener(SDK_READY_EVENT, handleWishlistSdkReady);
    scheduleWishlistSdkPatch();
  }

  buttonState.scheduleObserver();
  subscribePdpVariantStateChanges();

  pdpInitialSyncController.start();

  window.AuremePdpWishlistToast = {
    initialized: true,
  };
})();
